import { useMemo, useCallback } from 'react';
import GridLayout, { type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DashboardCard } from './DashboardCard';
import { useCardData } from '../../hooks/useDashboardData';
import type { SavedCard } from '../../types/analytics.types';
import type { GridLayoutItem, CardSizePreset } from '../../types/dashboard.types';
import { DEFAULT_GRID_CONFIG, getSizePresetDimensions } from '../../types/dashboard.types';
import { useIsMobile } from '@/hooks/useIsMobile';

interface DashboardGridProps {
  cards: SavedCard[];
  isEditMode: boolean;
  selectedCardId: string | null;
  containerWidth: number;
  onLayoutChange: (layout: GridLayoutItem[]) => void;
  onCardSelect: (cardId: string | null) => void;
  onCardDelete: (cardId: string) => void;
  onCardSizeChange: (cardId: string, preset: CardSizePreset) => void;
}

function CardWrapper({
  card,
  isEditMode,
  isSelected,
  onSelect,
  onDelete,
  onSizeChange,
}: {
  card: SavedCard;
  isEditMode: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onSizeChange: (preset: CardSizePreset) => void;
}) {
  const { data: result, isLoading, isError, refetch } = useCardData(card);

  return (
    <DashboardCard
      card={card}
      result={result ?? null}
      isLoading={isLoading}
      isError={isError}
      isEditMode={isEditMode}
      isSelected={isSelected}
      onSelect={onSelect}
      onDelete={onDelete}
      onRefresh={refetch}
      onSizeChange={onSizeChange}
    />
  );
}

export function DashboardGrid({
  cards,
  isEditMode,
  selectedCardId,
  containerWidth,
  onLayoutChange,
  onCardSelect,
  onCardDelete,
  onCardSizeChange,
}: DashboardGridProps) {
  const isMobile = useIsMobile();
  const layout = useMemo<GridLayoutItem[]>(
    () =>
      cards.map((card) => ({
        i: card.id,
        x: card.position.x,
        y: card.position.y,
        w: card.position.w,
        h: card.position.h,
        minW: 2,
        minH: 2,
      })),
    [cards]
  );

  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      const mappedLayout: GridLayoutItem[] = newLayout.map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      }));

      const hasChanges = mappedLayout.some((item) => {
        const original = layout.find((l) => l.i === item.i);
        if (!original) return true;
        return (
          original.x !== item.x ||
          original.y !== item.y ||
          original.w !== item.w ||
          original.h !== item.h
        );
      });

      if (hasChanges) {
        onLayoutChange(mappedLayout);
      }
    },
    [layout, onLayoutChange]
  );

  const handleCardSizeChange = useCallback(
    (cardId: string, preset: CardSizePreset) => {
      onCardSizeChange(cardId, preset);
      const dimensions = getSizePresetDimensions(preset);
      const currentLayout = layout.find((l) => l.i === cardId);
      if (currentLayout) {
        const newLayout = layout.map((item) =>
          item.i === cardId ? { ...item, w: dimensions.w, h: dimensions.h } : item
        );
        onLayoutChange(newLayout);
      }
    },
    [layout, onLayoutChange, onCardSizeChange]
  );

  // Phones: a 12-column drag grid is unusable at 375px, so render the cards
  // as a plain vertical stack (ordered by saved position), non-draggable.
  // Card height mirrors the saved grid height so charts keep their proportions.
  if (isMobile) {
    const sorted = [...cards].sort(
      (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
    );
    const [, marginY] = DEFAULT_GRID_CONFIG.margin;
    return (
      <div className="flex flex-col gap-4 min-w-0">
        {sorted.map((card) => {
          const h = Math.max(card.position.h, 2);
          const height = h * DEFAULT_GRID_CONFIG.rowHeight + (h - 1) * marginY;
          return (
            <div key={card.id} style={{ height }} className="min-w-0">
              <CardWrapper
                card={card}
                isEditMode={false}
                isSelected={false}
                onSelect={() => onCardSelect(card.id)}
                onDelete={() => onCardDelete(card.id)}
                onSizeChange={(preset) => handleCardSizeChange(card.id, preset)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <GridLayout
      className="layout"
      layout={layout}
      width={containerWidth}
      gridConfig={{
        cols: DEFAULT_GRID_CONFIG.cols,
        rowHeight: DEFAULT_GRID_CONFIG.rowHeight,
        margin: DEFAULT_GRID_CONFIG.margin,
        containerPadding: DEFAULT_GRID_CONFIG.containerPadding,
      }}
      dragConfig={{
        enabled: isEditMode,
        handle: '.drag-handle',
      }}
      resizeConfig={{
        enabled: isEditMode,
      }}
      onLayoutChange={handleLayoutChange}
    >
      {cards.map((card) => (
        <div key={card.id}>
          <CardWrapper
            card={card}
            isEditMode={isEditMode}
            isSelected={selectedCardId === card.id}
            onSelect={() => onCardSelect(card.id)}
            onDelete={() => onCardDelete(card.id)}
            onSizeChange={(preset) => handleCardSizeChange(card.id, preset)}
          />
        </div>
      ))}
    </GridLayout>
  );
}
